import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import { adminWorkflowRunsQueryKeys, showAdminWorkflowRunHealth } from 'services/api/admin/workflowRuns';
import { colors } from 'styles/theme/colors';

import { Subtitle, Title } from './runPresentation';

// Where work is piling up, across every client. Three groupings answering three
// different questions: which STEP accumulates work, whether the ENGINE is healthy, and which
// CLIENT is affected.
export default function RunHealth() {
  const { data, isLoading } = useQuery({
    queryKey: adminWorkflowRunsQueryKeys.health(),
    queryFn: ({ signal }) => showAdminWorkflowRunHealth(signal),
  });

  if (isLoading || !data) return <Page>Loading…</Page>;

  return (
    <Page>
      {/* This page is reachable from the Runs tab strip and was otherwise a dead end — the only
          way back was the browser button. Same crumb the run detail page uses. */}
      <Crumb to='/admin/workflows/runs'>Workflow Runs</Crumb>
      <Title>Workflow Health</Title>
      <Subtitle>Every client. The &ldquo;now&rdquo; columns count the runs this console lists.</Subtitle>

      <Section>Engine</Section>
      <Table>
        <thead>
          <tr>
            <th>Signal</th>
            <th>Runs</th>
            <th>What it means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Changes never processed</td>
            <td>{data.engine.unsettled}</td>
            <td>
              <Subtitle>An update landed and no evaluation has cleared it since.</Subtitle>
            </td>
          </tr>
          <tr>
            <td>Stopped part-way through processing</td>
            <td>{data.engine.leaseHeld}</td>
            <td>
              <Subtitle>A pass claimed the run and never released it.</Subtitle>
            </td>
          </tr>
          <tr>
            <td>On an older version</td>
            <td>{data.engine.behindVersion}</td>
            <td>
              <Subtitle>
                Started on an older version of the workflow. Some are waiting on values no current step produces.
              </Subtitle>
            </td>
          </tr>
        </tbody>
      </Table>

      <Section>By step</Section>
      <Table>
        <thead>
          <tr>
            <th>Step</th>
            <th>Errored (7 days)</th>
            <th>Processing now</th>
            <th>Waiting on a person</th>
            <th>Flagged now</th>
          </tr>
        </thead>
        <tbody>
          {data.byStep.map((row) => (
            <tr key={row.actionType}>
              <td>{row.actionLabel}</td>
              {/* A failure count with no denominator says nothing about whether a step is broken
                  or simply busy. */}
              <td>{row.errored > 0 ? `${row.errored} of ${row.started}` : <Muted>None</Muted>}</td>
              <td>{row.running || <Muted>0</Muted>}</td>
              <td>{row.actionRequired || <Muted>0</Muted>}</td>
              <td>{row.flagged || <Muted>0</Muted>}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Section>By client</Section>
      <Table>
        <thead>
          <tr>
            <th>Client</th>
            <th>Open runs</th>
          </tr>
        </thead>
        <tbody>
          {data.byClient.map((row) => (
            <tr key={row.client}>
              <td>{row.client}</td>
              <td>{row.open}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Page>
  );
}

// See RunDetail: ContentContainer clips rather than scrolls, so each page scrolls itself.
const Crumb = styled(Link)`
  display: block;
  font-size: 12px;
  color: ${colors.blue100};
  margin-bottom: 4px;
`;

const Page = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 24px 28px 80px;
`;

// Bare <h2> left these at the user-agent default: oversized, bold, and with collapsing margins
// that sat them right on top of the table above. The space above a section heading is what
// separates one table from the next, so it is deliberately much larger than the space below.
const Section = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 36px 0 0;
`;

const Muted = styled.span`
  color: ${colors.black50};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 14px;
  margin-top: 8px;

  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 12px;
    color: ${colors.black50};
    border-bottom: 1px solid ${colors.black10};
    white-space: nowrap;
  }

  td {
    padding: 12px 14px;
    border-bottom: 1px solid ${colors.black5};
    vertical-align: top;
  }
`;
